<template>
  <div class="content content_in mb-5 mb-md-0">
    <div class="row">
      <div class="col-12 col-xl-7">
        <div class="title-row row justify-content-between">
          <div class="col-12 col-md-auto">
            <h1>
              {{ $t("platform.create_new") }}
            </h1>
          </div>
        </div>

        <VideoGuide v-if="!finished" class="d-xl-none mb-2" :title="$t('platform.how_to_setup_guide')" :description="$t('platform.video')" :videoLink="siteVideoGuide" />

        <GuideLinks v-if="!finished" class="d-xl-none mb-2" />

        <div v-if="finished" class="section created">
          <h2>{{ $t("platform.site.created.title") }}</h2>
          <p>{{ $t("platform.site.created.text") }}</p>
          <div class="site-button-wrapper">
            <router-link :to="{ name: 'PlatformCard', params: { id: platformID, tab: 'formats' } }" class="btn _mob100">
              {{ $t("platform.site.created.to_formats") }}
            </router-link>
            <router-link :to="{ name: 'PlatformCard', params: { id: platformID, tab: 'general' } }" class="btn _mob100 _border">
              {{ $t("platform.site.created.to_verify") }}
            </router-link>
          </div>
        </div>
        <template v-else>
          <StepperBar :activeStep="creatingSiteActiveStep" :stepList="creatingSiteStepList" />
          <div class="sep _h10"></div>
          <div v-show="creatingSiteActiveStep === 1">
            <InformationBlock v-if="showSatelliteHint" class="satellite-hint mb-4">
              {{ $t("platform.site.satellite_hint.text") }}
              <router-link :to="{ name: 'Satellites' }">{{ $t("platform.site.satellite_hint.link") }}</router-link>
            </InformationBlock>
            <NameField ref="name" v-model="platform.name" :v="$v.platform.name" />
            <PaymentModelField v-model="platform.payment_model" :invalid="$v.platform.payment_model.$error" />
            <SettingsField :label="$t('platform.create.url')" :hint="$t('platform.card.fields.url_create')">
              <SelectInput v-model.trim="platform.url" :selectValues="['https://', 'http://']" :invalid="$v.platform.url.$invalid && platform.url !== '' && platform.url !== 'http://' && platform.url !== 'https://'" />
              <div class="warn-block mt-2" v-if="detectedBlogspot">
                <svg class="icon" v-svg symbol="info" size="0 0 17 17" role="info"></svg>
                <p>
                  {{ $t("platform.site.confirm_site.blogspot_warn") }}
                </p>
              </div>
              <div class="warn-block mt-2" v-if="detectedWeebly">
                <svg class="icon" v-svg symbol="info" size="0 0 17 17" role="info"></svg>
                <p>
                  {{ $t("platform.site.confirm_site.weebly_warn") }}
                </p>
              </div>

              <div class="warn-block mt-2" v-if="platform.url === 'http://'">
                <svg class="icon" v-svg symbol="info" size="0 0 17 17" role="info"></svg>
                <p>
                  {{ $t("platform.site.confirm_site.http_warn") }}
                </p>
              </div>
              <small v-if="$v.platform.url.$invalid && !$v.platform.url.minLength && platform.url !== '' && platform.url !== 'http://' && platform.url !== 'https://'" class="error">
                {{ $t("min_len_pass", { number: "3" }) }}
              </small>
              <small v-else-if="$v.platform.url.$invalid && !$v.platform.url.maxLength" class="error">
                {{ $t("max_len_pass", { number: 126 }) }}
              </small>
              <small v-else-if="$v.platform.url.$invalid && !$v.platform.url.url && platform.url !== '' && platform.url !== 'http://' && platform.url !== 'https://'" class="error">
                {{ $t("platform.url_invalid") }}
              </small>
            </SettingsField>
          </div>
          <div v-show="creatingSiteActiveStep === 2">
            <MailingFields :form="platform" :options="{ mailings, pushIntervals, pushDelays: push_delays }" :selectAllSchedule="selectAllSchedule" />
            <SettingsField :label="$t('category')" :hint="$t('platform.card.fields.category_create')">
              <custom-multiselect v-model="platform.category_id" :list="categories" :allowEmpty="false">
                <template v-slot:block="props">
                  <span>
                    {{ props.value.title }}
                  </span>
                </template>
                <template v-slot:list="props">
                  <span>
                    {{ props.value.title }}
                  </span>
                </template>
              </custom-multiselect>
            </SettingsField>
            <SettingsField :label="$t('platform.card.fields.adult_title')" :hint="$t('platform.card.fields.adult')">
              <CheckBoxWrapper v-model="platform.adult" :text="$t('platform.site.show_for_adult')" />
            </SettingsField>
            <SettingsField v-if="platform.payment_model && platform.payment_model.value === 0" :label="$t('platform.sales_on_feeds')" :hint="$t('platform.card.fields.feeds')">
              <CheckBoxWrapper v-model="platform.feedsSales" :text="$t(platform.feedsSales ? 'platform.card.on' : 'platform.card.off')" />
            </SettingsField>
            <DisabledCategoriesField v-if="platform.payment_model && platform.payment_model.title !== 'CPL'" v-model="platform.disabledCategory" :categories="disabledCategories" />
            <PostbackFields :form="platform" :formatList="postback_format_list" :invalid="$v.platform.postback_url.$invalid" />
          </div>
          <div class="row pt-1">
            <div class="col-12">
              <div class="form-item my-4 pb-0 site-button-wrapper">
                <button v-if="creatingSiteActiveStep !== 1" @click.prevent="changeStep(creatingSiteActiveStep - 1)" class="btn _mob100 _bg-gray">
                  <svg v-svg role="presentation" size="0 0 20 14" symbol="prev"></svg>
                  {{ $t("back") }}
                </button>
                <button v-if="creatingSiteActiveStep !== creatingSiteStepList.length" :disabled="disableNextButtonOnFirstStep" @click.prevent="changeStep(creatingSiteActiveStep + 1)" class="btn _mob100">
                  {{ $t("continue") }}
                  <svg v-svg role="presentation" size="0 0 20 14" symbol="next"></svg>
                </button>
                <button @click="createSite" :disabled="allSelectedCategories" v-if="creatingSiteActiveStep == creatingSiteStepList.length" class="btn _mob100">
                  {{ $t("save") }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="d-none d-xl-block col-xl-5">
        <VideoGuide :title="$t('platform.how_to_setup_guide')" :description="$t('platform.video')" :videoLink="siteVideoGuide" />

        <GuideLinks />
      </div>
    </div>
  </div>
</template>
<script>
import "@/assets/css/_page-platform.scss";
import "@/assets/css/_section.scss";
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength, url } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import debounce from "@/mixins/debounce";
import ServiceSubAccs from "@/api/subaccsService";
import ServicePanel from "@/api/servicePanel";
import serviceDomain from "@/api/serviceDomain";
import SelectInput from "@/components/SelectInput.vue";
import InformationBlock from "@/components/InformationBlock.vue";
import VideoGuide from "@/components/VideoGuide.vue";
import GuideLinks from "@/components/GuideLinks.vue";
import CheckBoxWrapper from "@/components/CheckBoxWrapper.vue";
import StepperBar from "@/components/StepperBar.vue";
import SettingsField from "@/components/PlatformCard/SettingsField.vue";
import NameField from "@/components/PlatformCard/fields/NameField.vue";
import PaymentModelField from "@/components/PlatformCard/fields/PaymentModelField.vue";
import DisabledCategoriesField from "@/components/PlatformCard/fields/DisabledCategoriesField.vue";
import MailingFields from "@/components/PlatformCard/fields/MailingFields.vue";
import PostbackFields from "@/components/PlatformCard/fields/PostbackFields.vue";
import { allCategoriesDisabled, CATEGORIES } from "@/components/PlatformCard/subaccountPayload";

export default {
  name: "Site",
  mixins: [validationMixin, debounce],
  components: {
    InformationBlock,
    SelectInput,
    VideoGuide,
    GuideLinks,
    CheckBoxWrapper,
    StepperBar,
    SettingsField,
    NameField,
    PaymentModelField,
    DisabledCategoriesField,
    MailingFields,
    PostbackFields,
  },
  data() {
    return {
      selectAllSchedule: true,
      currentPlatform: null,
      selectedPlatform: null,
      detectedBlogspot: false,
      detectedWeebly: false,

      createSiteType: null,

      platform: {
        name: "",
        url: "https://",
        payment_model: {
          title: "Revshare",
          value: 0,
        },
        postback_url: "",
        disabledCategory: [],
        category_id: { title: "Adult", value: 1 },
        adult: true,
        feedsSales: false,
        mailing_frequency: { title: "Standard", value: "standart" },
        push_send_interval: {},
        push_delay_interval: {},
        schedule_days: [],
        postback_format: [],
      },
      disabledCategories: [],
      categories: CATEGORIES,
      mailings: [],
      creatingSiteStepList: [
        { id: 1, title: "platform.site.link", value: "link" },
        { id: 2, title: "platform.site.settings", value: "settings" },
      ],
      creatingSiteActiveStep: 1,
      // The platform is created, the steps give way to what to do next
      finished: false,
      postback_format_list: [
        {
          title: "Push",
          id: 0,
          value: "push",
        },
        {
          title: "InPage",
          id: 1,
          value: "in_page",
        },
        {
          title: "OnClick",
          id: 2,
          value: "on_click",
        },
      ],
      pushIntervals: [],
      push_delays: [],
      platformID: null,
    };
  },
  watch: {
    "$i18n.locale": function () {
      this.getPushDelays();
      this.getPushIntervals();
    },
    "platform.url": function (val) {
      /(blogspot.com)/i.test(val) ? (this.detectedBlogspot = true) : (this.detectedBlogspot = false);
      /(weebly.com)/i.test(val) ? (this.detectedWeebly = true) : (this.detectedWeebly = false);
    },
    platform: {
      deep: true,
      handler() {
        if (this.platformID) {
          if (!this.debounceValue) {
            this.debounceValue = this.debounce(this.updatePlatformData, 1000);
          }
          this.debounceValue();
        }
      },
    },
  },
  validations() {
    return {
      platform: {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(52),
        },
        url: {
          url,
          required,
          minLength: minLength(3),
          maxLength: maxLength(126),
        },
        postback_url: {
          url,
          maxLength: maxLength(4096),
        },
        payment_model: {
          required,
        },
      },
    };
  },
  async mounted() {
    await this.getMailingFrequency();
    await this.getPushIntervals();
    await this.getPushDelays();

    if (this.$route.path.includes("site")) {
      this.createSiteType = "site";
    } else {
      this.createSiteType = "landing";
    }
    this.creatingSiteActiveStep = Number(this.$route.params.step) || -1;

    this.selectedPlatform = this.$route.fullPath.includes("site") ? 1 : 2;

    if (this.$route.query.id) {
      this.platformID = +this.$route.query.id;
    }

    // Continuing a platform whose creation was left at some step
    if (this.$route.query.id) {
      const id = this.$route.query.id;
      ServiceSubAccs.getSubAcc(id)
        .then((res) => {
          if (res && res.status === 200) {
            this.currentPlatform = res.data;
          }
        })
        .then(() => {
          this.getCategories().then(() => {
            if (this.currentPlatform) {
              this.platform = {
                name: this.currentPlatform.name,
                payment_model: this.currentPlatform.payment_model == 1 ? { title: "CPL", value: 1 } : { title: "Revshare", value: 0 },
                url: this.currentPlatform.url,
                schedule_days: this.currentPlatform.schedule_days,
                disabledCategory: this.disabledCategories.filter((item) => this.currentPlatform.disabled_category_ids.includes(item.id)) || [],
                postback_url: this.currentPlatform.postback_url,
                category_id: this.categories.find((el) => el.value === this.currentPlatform.category_id),
                push_send_interval: this.pushIntervals.find((el) => el.value === this.currentPlatform.push_send_interval) || this.pushIntervals[0],
                push_delay_interval: this.push_delays.find((el) => el.value === this.currentPlatform.push_delay_interval) || this.push_delays[0],
                mailing_frequency: this.mailings.find((el) => el.value === this.currentPlatform.mailing_frequency) || { title: "Custom", value: "custom" },
                adult: Boolean(this.currentPlatform.adult),
                feedsSales: this.currentPlatform.deny_sell_to_feeds,
                postback_format: [],
              };
              this.setPostbackValue();
            }
          });
        })
        .finally(() => {
          if (this.currentPlatform.current_step) {
            // Drafts left on the former confirmation step continue with the settings
            this.creatingSiteActiveStep = Math.min(this.currentPlatform.current_step, this.creatingSiteStepList.length);
            this.changeUrl();
          }
        });
    } else {
      this.getCategories();
    }
  },
  methods: {
    updatePlatformData(isFinish = false) {
      let params = {
        name: this.platform.name,
        disabled_category_ids: this.platform.disabledCategory.map((el) => el.id) || [],
        payment_model: +this.platform.payment_model.value,
        mailing_frequency: this.platform.mailing_frequency?.value || "standart",
        push_delay_interval: this.platform.push_delay_interval?.value || 0,
        push_send_interval: this.platform.push_send_interval?.value || null,
        schedule_days: this.platform.schedule_days,
        postback_url: this.platform.postback_url,
        adult: +this.platform.adult,
        deny_sell_to_feeds: this.platform.payment_model.value === 0 ? +this.platform.feedsSales : 0,
        enable_postback_in_page_revshare: this.checkPostbackValue("in_page"),
        enable_postback_on_click_revshare: this.checkPostbackValue("on_click"),
        enable_postback_push_revshare: this.checkPostbackValue("push"),
        url: this.platform.url,
        category_id: this.platform.category_id.value,
        site: this.selectedPlatform,
        current_step: +this.$route.params.step || 1,
      };
      if (isFinish) {
        params.current_step = null;
        ServiceSubAccs.finishSubAcc({
          id: this.platformID,
          params: params,
        }).then((res) => {
          if (res && res.status === 200) {
            this.$alert({
              title: this.$t("sent_success"),
              text: res.message,
              type: "success",
            });
            this.finished = true;
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          this.$alert({
            title: this.$t("sent_error"),
            text: res.message,
          });
        });
        return;
      }
      if (this.platformID) {
        ServiceSubAccs.updateSubAcc({
          id: this.platformID,
          params: params,
        });
      } else {
        ServiceSubAccs.createSubAcc(params).then((res) => {
          if (res && res.status === 200) {
            this.platformID = res.data.id;
          }
        });
      }
    },
    getMailingFrequency() {
      return ServicePanel.getMailingFrequency().then((res) => {
        if (res && res.status === 200) {
          this.mailings = res.data;
        } else {
          this.$alert({
            type: "error",
            title: this.$t("sent_error"),
            text: res.message,
          });
        }
      });
    },
    getPushDelays() {
        if (this.featureFlags && this.featureFlags.includes('push_delay_interval')){
      return ServicePanel.getPushDelays().then((res) => {
        if (res && res.status === 200) {
          this.push_delays = res.data;
          this.platform.push_delay_interval = this.push_delays[0];
        } else {
          this.$alert({
            type: "error",
            title: this.$t("sent_error"),
            text: res?.message || "",
          });
        }
      });
       }
    },
    getPushIntervals() {
        if (this.featureFlags && this.featureFlags.includes('push_delay_interval')){
      return serviceDomain.getPushIntervals().then((res) => {
        if (res && res.status === 200) {
          this.pushIntervals = res.data;
          this.platform.push_send_interval = this.pushIntervals[0];
        } else {
          this.$alert({
            type: "error",
            title: this.$t("sent_error"),
            text: res?.message || "",
          });
        }
      });
        }
    },
    changeUrl() {
      const platformType = this.$route.path.includes("site") ? "site" : "landing";
      if (+this.$route.params.step !== this.creatingSiteActiveStep) {
        this.$router.push({
          path: `/create-platform/${platformType}/${this.creatingSiteActiveStep}`,
          query: this.$route.query,
        });
      }
    },
    changeStep(step) {
      this.creatingSiteActiveStep = step;
      this.changeUrl();
      this.updatePlatformData();
    },
    async getCategories() {
      await ServicePanel.getCategories().then((res) => {
        if (res && res.data) {
          this.disabledCategories.push(...res.data);
        }
      });
    },
    setPostbackValue() {
      const map = new Map([
        ["push", "enable_postback_push_revshare"],
        ["in_page", "enable_postback_in_page_revshare"],
        ["on_click", "enable_postback_on_click_revshare"],
      ]);

      map.forEach((value, key) => {
        if (this.currentPlatform[value]) {
          this.platform.postback_format.push(this.postback_format_list.find((el) => el.value === key));
        }
      });
    },
    checkPostbackValue(format) {
      return this.platform.postback_format.some((el) => el.value === format);
    },
    createSite() {
      this.$v.$touch();
      if (this.$v.$invalid || this.allSelectedCategories) {
        this.$gtm.trackEvent({ event: "create_source_disabled_button" });
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return;
      }
      this.$gtm.trackEvent({ event: "create_source_submited" });
      this.updatePlatformData(true);
      this.$v.$reset();
    },
  },
  computed: {
    ...mapGetters({
      user: "user/getUser",
      featureFlags: "user/getFeatureFlags",
    }),
    disableNextButtonOnFirstStep() {
      return this.creatingSiteActiveStep == 1 && (this.$v.platform.name.$invalid || this.$v.platform.url.$invalid);
    },
    // Satellites serve landings without a server and a tracker of the user's own
    showSatelliteHint() {
      return this.createSiteType === "landing" && this.featureFlags && this.featureFlags.includes("satellite");
    },
    siteVideoGuide() {
      return this.$i18n.locale === "ru" ? "https://youtu.be/LDEEct4tJwg" : "https://youtu.be/6bAKJql9uRE";
    },
    allSelectedCategories() {
      return allCategoriesDisabled(this.platform.disabledCategory);
    },
  },
};
</script>
<style scoped lang="scss">
.warn-block {
  display: flex;
  align-items: start;
  column-gap: 6px;
  p {
    margin: 0;
    font-size: 14px;
    color: #c62424;
  }
  svg {
    width: 30px;
    fill: #c62424;
  }
}

.site-button-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.satellite-hint a {
  color: #37807d;
  text-decoration: underline;
}

.created {
  p {
    margin-bottom: 24px;
    font-size: 15px;
    line-height: 1.5;
  }
}
</style>
